import { X, Star, ShoppingCart, Check, Package, Tag, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { Product } from './ProductCard';
import { useCart } from '../contexts/CartContext';
import { cn, formatPrice } from '../lib/utils';

interface ProductModalProps {
  product: Product | null;
  onClose: () => void;
}

function getProductImages(product: Product): { url: string; label: string }[] {
  const baseUrl = product.image_url;
  const match = baseUrl.match(/^(.+\/)(prod_\d+)(\.webp)(\?.*)?$/);

  if (!match) {
    return [{ url: baseUrl, label: 'Product' }];
  }

  const [, prefix, prodId, ext, version] = match;
  const v = version || '?v=12';

  return [
    { url: `${prefix}${prodId}${ext}${v}`, label: 'Product' },
    { url: `${prefix}${prodId}_angle${ext}${v}`, label: 'Alternatief' },
    { url: `${prefix}${prodId}_lifestyle${ext}${v}`, label: 'In gebruik' },
  ];
}

export default function ProductModal({ product, onClose }: ProductModalProps) {
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());
  const [zoomed, setZoomed] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });

  const images = useMemo(() => product ? getProductImages(product) : [], [product]);

  useEffect(() => {
    setCurrentImageIndex(0);
    setFailedImages(new Set());
    setZoomed(false);
  }, [product?.id]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
      } else if (e.key === 'ArrowRight') {
        setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [images.length]);

  useEffect(() => {
    const handleNext = () => {
      setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    };
    const handlePrevious = () => {
      setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    };
    window.addEventListener('product-image-next', handleNext);
    window.addEventListener('product-image-previous', handlePrevious);
    return () => {
      window.removeEventListener('product-image-next', handleNext);
      window.removeEventListener('product-image-previous', handlePrevious);
    };
  }, [images.length]);

  useEffect(() => {
    if (product) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [product]);

  if (!product) return null;

  const availableImages = images.filter((_, idx) => !failedImages.has(idx));

  const handleAddToCart = () => {
    addToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl max-h-[95vh] overflow-auto rounded-2xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-card/80 hover:bg-card text-muted-foreground hover:text-foreground transition-colors border border-border"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex flex-col md:flex-row">
          {/* Image Carousel */}
          <div className="relative w-full md:w-3/5 bg-background/30 flex flex-col">
            <div
              className={cn('relative flex items-center justify-center overflow-hidden', zoomed ? 'cursor-zoom-out' : 'cursor-zoom-in')}
              style={{ minHeight: '500px' }}
              onClick={() => setZoomed(!zoomed)}
              onMouseMove={(e) => {
                if (zoomed) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - rect.left) / rect.width) * 100;
                  const y = ((e.clientY - rect.top) / rect.height) * 100;
                  setMousePos({ x, y });
                }
              }}
              onMouseLeave={() => setMousePos({ x: 50, y: 50 })}
            >
              <img
                src={images[currentImageIndex]?.url || product.image_url}
                alt={`${product.name} - ${images[currentImageIndex]?.label || 'Product'}`}
                className={cn(
                  'transition-transform duration-100',
                  zoomed ? 'scale-[2.5] cursor-zoom-out' : 'max-w-full max-h-[500px] object-contain cursor-zoom-in',
                )}
                style={zoomed ? { transformOrigin: `${mousePos.x}% ${mousePos.y}%` } : undefined}
                onError={(e) => {
                  setFailedImages((prev) => new Set([...prev, currentImageIndex]));
                  if (currentImageIndex > 0) {
                    setCurrentImageIndex(0);
                  }
                  (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x400?text=No+Image';
                }}
              />

              {/* Zoom button */}
              <button
                onClick={(e) => { e.stopPropagation(); setZoomed(!zoomed); }}
                className="absolute bottom-4 right-4 p-2 rounded-full bg-card/80 hover:bg-card text-foreground shadow-lg transition-all hover:scale-110 border border-border"
                aria-label={zoomed ? 'Zoom uit' : 'Zoom in'}
              >
                {zoomed ? <ZoomOut className="w-5 h-5" /> : <ZoomIn className="w-5 h-5" />}
              </button>

              {/* Navigation arrows */}
              {availableImages.length > 1 && !zoomed && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1)); }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-card/80 hover:bg-card text-foreground shadow-lg transition-all hover:scale-110 border border-border"
                    aria-label="Vorige foto"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0)); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-card/80 hover:bg-card text-foreground shadow-lg transition-all hover:scale-110 border border-border"
                    aria-label="Volgende foto"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}

              {/* Out of stock overlay */}
              {!product.in_stock && (
                <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                  <span className="text-lg font-medium text-muted-foreground">Uitverkocht</span>
                </div>
              )}
            </div>

            {/* Thumbnail dots */}
            {images.length > 1 && !zoomed && (
              <div className="flex items-center justify-center gap-3 py-4 bg-background/20">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    disabled={failedImages.has(idx)}
                    className={cn(
                      'transition-all',
                      failedImages.has(idx)
                        ? 'opacity-30 cursor-not-allowed'
                        : currentImageIndex === idx
                        ? 'ring-2 ring-primary ring-offset-2 ring-offset-card'
                        : 'hover:opacity-100 opacity-70',
                    )}
                    aria-label={img.label}
                    title={img.label}
                  >
                    <div className={cn(
                      'w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors',
                      currentImageIndex === idx ? 'border-primary' : 'border-transparent',
                    )}>
                      <img
                        src={img.url}
                        alt={img.label}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          setFailedImages((prev) => new Set([...prev, idx]));
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Image label */}
            {images.length > 1 && !zoomed && (
              <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-card/80 text-xs font-medium text-muted-foreground border border-border">
                {images[currentImageIndex]?.label}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="w-full md:w-2/5 p-8 flex flex-col">
            <p className="text-sm text-primary font-medium uppercase tracking-wide mb-1">
              {product.brand}
            </p>
            <h2 className="text-2xl font-bold text-foreground mb-3">
              {product.name}
            </h2>
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center gap-1">
                <Star className="w-5 h-5 fill-primary text-primary" />
                <span className="font-medium text-foreground">{product.rating.toFixed(1)}</span>
              </div>
              <span className="text-muted-foreground/40">|</span>
              <span className="text-sm text-muted-foreground capitalize">{product.category}</span>
            </div>
            <div className="text-3xl font-bold text-foreground mb-4">
              {formatPrice(product.price)}
            </div>
            <div className="mb-6 flex-grow">
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Beschrijving</h3>
              <p className="text-foreground/80 leading-relaxed">
                {product.description || 'Geen beschrijving beschikbaar.'}
              </p>
            </div>
            <div className="flex items-center gap-2 mb-4 text-sm">
              <Package className="w-4 h-4 text-muted-foreground" />
              {product.in_stock ? (
                <span className="text-success">
                  Op voorraad ({product.stock_quantity} stuks)
                </span>
              ) : (
                <span className="text-destructive">Niet op voorraad</span>
              )}
            </div>
            {product.tags && product.tags.length > 0 && (
              <div className="flex items-start gap-2 mb-6">
                <Tag className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div className="flex flex-wrap gap-1">
                  {product.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <button
              onClick={handleAddToCart}
              disabled={!product.in_stock}
              className={cn(
                'btn-primary w-full py-3 px-6 rounded-xl font-medium flex items-center justify-center gap-2',
                added ? 'bg-success text-white' : 'bg-primary text-primary-foreground hover:bg-primary/90',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              {added ? (
                <>
                  <Check className="w-5 h-5" />
                  Toegevoegd!
                </>
              ) : (
                <>
                  <ShoppingCart className="w-5 h-5" />
                  In winkelwagen
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
